import { useReducer } from "react";
import type { ChangeOrderCause } from "../constants/causes";
import { isHttpUrl } from "../constants/url";
import { sourceItemsFor } from "./payload";
import type { RevisedMap, RevisedValues, ScopeLine } from "./types";

export type DraftMode = "scope" | "tracked";

export interface ChangeOrderDraft {
  mode: DraftMode;
  cause: ChangeOrderCause | "";
  causeNote: string;
  externalReference: string;
  externalUrl: string;
  revised: RevisedMap;
  governance: Record<string, number>; // parent fixed governance id → hours
  trackedHours: string;
  trackedRevenue: string;
}

export type DraftAction =
  | {
      type: "setField";
      field:
        | "cause"
        | "causeNote"
        | "externalReference"
        | "externalUrl"
        | "trackedHours"
        | "trackedRevenue";
      value: string;
    }
  | { type: "setMode"; mode: DraftMode }
  | { type: "reviseLine"; key: string; values: RevisedValues }
  | { type: "setGovernance"; id: string; hours: number | null };

export const emptyDraft: ChangeOrderDraft = {
  mode: "scope",
  cause: "",
  causeNote: "",
  externalReference: "",
  externalUrl: "",
  revised: {},
  governance: {},
  trackedHours: "",
  trackedRevenue: "",
};

const withoutUndefined = (values: RevisedValues): RevisedValues =>
  Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== undefined)
  ) as RevisedValues;

// Switching mode keeps both sides' values so switching back restores them.
export const draftReducer = (
  state: ChangeOrderDraft,
  action: DraftAction
): ChangeOrderDraft => {
  switch (action.type) {
    case "setField":
      return { ...state, [action.field]: action.value };
    case "setMode":
      return { ...state, mode: action.mode };
    case "reviseLine":
      return {
        ...state,
        revised: {
          ...state.revised,
          [action.key]: withoutUndefined({
            ...state.revised[action.key],
            ...action.values,
          }),
        },
      };
    case "setGovernance": {
      const governance = { ...state.governance };
      if (action.hours === null) delete governance[action.id];
      else governance[action.id] = action.hours;
      return { ...state, governance };
    }
    default:
      return state;
  }
};

export const useChangeOrderDraft = () => useReducer(draftReducer, emptyDraft);

export interface DraftErrors {
  cause?: string;
  externalUrl?: string;
  trackedHours?: string;
  trackedRevenue?: string;
  lines?: string;
}

export const NO_LINES_ERROR = "Change at least one line, or choose Tracked in another system.";

export const amountError = (value: string, missing: string) => {
  if (value.trim() === "") return missing;
  const n = Number(value);
  return isNaN(n) || n < 0 ? "Enter zero or more" : undefined;
};

// Pass `lines` to also require, in scope mode, at least one line that adds hours.
export const validateDraft = (draft: ChangeOrderDraft, lines?: ScopeLine[]): DraftErrors => {
  const errors: DraftErrors = {};
  if (!draft.cause) errors.cause = "Select a cause";
  const url = draft.externalUrl.trim();
  if (url && !isHttpUrl(url)) errors.externalUrl = "Enter an http(s) URL";
  if (draft.mode === "tracked") {
    const hours = amountError(draft.trackedHours, "Enter hours");
    const revenue = amountError(draft.trackedRevenue, "Enter revenue");
    if (hours) errors.trackedHours = hours;
    if (revenue) errors.trackedRevenue = revenue;
  } else if (lines && sourceItemsFor(lines, draft.revised).length === 0) {
    errors.lines = NO_LINES_ERROR;
  }
  return errors;
};
